import { forwardRef, useMemo } from "react";
import DatePicker from "react-datepicker";
import moment from "moment";
import { TextField, InputAdornment, IconButton } from "@mui/material";
import "react-datepicker/dist/react-datepicker.css";
import "./datepicker.css";
import { makeStyles } from "@material-ui/core";
import clsx from "clsx";
import CalendarTodaySharpIcon from "@mui/icons-material/CalendarTodaySharp";
import { createPortal } from "react-dom";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";

const useStyles = makeStyles(() => ({
  datepickerTextField: {
    height: 40,
    fontFamily: "'Montserrat'",
    fontSize: 14,
    lineHeight: "24px !important",
    color: "#6B778C !important",
    fontWeight: 400,
  },
}));

const DateInput = forwardRef(
  (
    { value, onClick, datepickerClass, placeholder, isClearable, onClear }: any,
    ref: any
  ) => {
    const classes = useStyles();

    return (
      <TextField
        disabled
        className={clsx(classes.datepickerTextField, datepickerClass)}
        ref={ref}
        id="datepicker-textfield"
        variant="outlined"
        name="email"
        fullWidth
        value={value}
        placeholder={placeholder}
        autoComplete={"off"}
        sx={{
          "& .MuiOutlinedInput-root": {
            backgroundColor: "#ffffff",
          },
        }}
        label={""}
        onClick={onClick}
        InputProps={{
          className: `${classes.datepickerTextField} ${datepickerClass}`,
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label="toggle password visibility"
                onClick={onClick}
                edge="end"
              >
                <CalendarTodaySharpIcon sx={{ color: "#000000" }} />
              </IconButton>
              {isClearable && value && (
                <IconButton aria-label="clear" onClick={onClear} edge="end">
                  <HighlightOffIcon sx={{ color: "red" }} />
                </IconButton>
              )}
            </InputAdornment>
          ),
        }}
      />
    );
  }
);

function AppDatePicker({
  setDate,
  selectedDate,
  datepickerClass,
  minDate,
  dateFormat,
  showTimeInput,
  showTimeSelect,
  isClearable,
  placeholder,
  readOnly = false,
  minTime,
  filterTime,
  blockedDates = [],
}: any) {
  const blockedDateSet = useMemo(
    () => new Set(blockedDates),
    [blockedDates]
  );
  const excludedDates = useMemo(
    () =>
      blockedDates.map((date: string) =>
        moment(date, "YYYY-MM-DD").startOf("day").toDate()
      ),
    [blockedDates]
  );

  const isBlockedDate = (date: Date) =>
    blockedDateSet.has(moment(date).format("YYYY-MM-DD"));

  return (
    <DatePicker
      calendarClassName="app-datepicker"
      showYearDropdown
      timeIntervals={15}
      popperContainer={({ children }) => {
        return document.getElementById("custom-modal-dialog")
          ? createPortal(
              children,
              document.getElementById("custom-modal-dialog")
            )
          : createPortal(children, document.body);
      }}
      selected={selectedDate}
      placeholderText={placeholder}
      onChange={(date) => setDate(date)}
      customInput={
        <DateInput
          isClearable={isClearable}
          datepickerClass={datepickerClass}
          onClear={() => setDate("")}
        />
      }
      minDate={minDate}
      dateFormat={dateFormat}
      showTimeInput={showTimeInput}
      showTimeSelect={showTimeSelect}
      readOnly={readOnly}
      excludeDates={excludedDates}
      dayClassName={(date) =>
        isBlockedDate(date) ? "app-datepicker__day--blocked" : undefined
      }
      renderDayContents={(day, date) =>
        date && isBlockedDate(date) ? (
          <span title="Slots are full for this date">{day}</span>
        ) : (
          day
        )
      }
      {...(minTime ? { minTime } : {})}
      {...(filterTime ? { filterTime } : {})}
    />
  );
}

export { AppDatePicker };
